/**
 * The product quick view drawer (PR 10, plan I5) — opened from any product
 * row's thumbnail, app-wide. One request (GET /api/ops/products/{uid}/
 * quick-view); every section links to its full page.
 *
 * The numbers are the same ones every product table shows (the product
 * snapshot): stock and days of cover are the Restock row's, money is the P&L's
 * over the last 30 complete days, status is Amazon's own where it has one.
 */
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Drawer } from '../overlay/Drawer'
import ProductThumb from '../ProductThumb'
import { CopyId } from '../CopyId'
import type { ProductQuickView, QuickViewTrendPoint } from '../../ops/lib/api'
import { useProductQuickViewData } from '../../lib/productSnapshot'
import { fmtInt, fmtMoney } from '../../lib/format'
import { pnlMoney, pnlPct, pnlSignClass } from '../../ops/lib/pnlFormat'
import { statusText } from '../../lib/productStandardColumns'
import {
  hubProductsLink, isInAppLink, nextPoText, onOrderLabel, stockHeadline, stockPools, windowCaption,
} from './quickViewText'

const RATING = new Intl.NumberFormat('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })

/** A small sales sparkline over the window (plain SVG, no chart library). */
function Sparkline({ points }: { points: QuickViewTrendPoint[] }) {
  if (points.length < 2) return null
  const W = 220
  const H = 36
  const max = Math.max(1, ...points.map((p) => p.sales))
  const step = W / (points.length - 1)
  const r1 = (v: number) => Math.round(v * 10) / 10
  const d = points.map((p, i) => `${r1(i * step)},${r1(H - (p.sales / max) * (H - 2) - 1)}`).join(' ')
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Daily sales over the window"
      className="text-accent">
      <polyline points={d} fill="none" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  )
}

function Section({ title, link, onNavigate, children }: {
  title: string
  link?: { href: string; label: string }
  onNavigate: () => void
  children: ReactNode
}) {
  return (
    <section className="border-t border-edge pt-3 mt-3 first:border-t-0 first:pt-0 first:mt-0">
      <div className="flex items-baseline justify-between gap-2 mb-1.5">
        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-ink-faint">{title}</h3>
        {link && <QvLink href={link.href} onNavigate={onNavigate}>{link.label}</QvLink>}
      </div>
      {children}
    </section>
  )
}

function QvLink({ href, onNavigate, children }: { href: string; onNavigate: () => void; children: ReactNode }) {
  if (isInAppLink(href)) {
    return <Link to={href} onClick={onNavigate} className="text-xs text-accent hover:underline">{children}</Link>
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-xs text-accent hover:underline">
      {children}
    </a>
  )
}

function Fact({ label, value, className, title }: { label: string; value: ReactNode; className?: string; title?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-[13px]" title={title}>
      <span className="text-ink-muted">{label}</span>
      <span className={'tabular-nums text-ink ' + (className ?? '')}>{value}</span>
    </div>
  )
}

/** The drawer's body for one product — pure render of the quick-view
 *  response (quickView.test.tsx renders it to a string). */
export function QuickViewBody({ q, onNavigate }: { q: ProductQuickView; onNavigate: () => void }) {
  const est = q.cogs_estimated
  const estTitle = est ? 'Estimated: some of the cost of goods in this window comes from the catalog cost sheet, not a received purchase order.' : undefined
  const pools = stockPools(q.stock_breakdown)
  const statuses = Object.entries(q.statuses ?? {})
  const links = q.links ?? {}
  return (
    <div className="flex flex-col">
      <div className="flex items-start gap-3 mb-3">
        <ProductThumb src={q.image_url} alt={q.product_name ?? q.uid} size={56} previewSize={260} />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-ink leading-snug">{q.product_name ?? q.uid}</div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-ink-muted">
            <CopyId value={q.uid} label="UID"><span className="font-mono">{q.uid}</span></CopyId>
            {q.asin && (
              <>
                <span aria-hidden>·</span>
                <CopyId value={q.asin} label="ASIN"><span className="font-mono">{q.asin}</span></CopyId>
              </>
            )}
          </div>
          <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-ink-muted">
            {q.status_summary && <span>{q.status_summary}</span>}
            {!q.status_summary && q.status && <span>{statusText(q.status)}</span>}
            {q.buy_box_pct != null && <span>Buy Box {pnlPct(q.buy_box_pct)}</span>}
            {q.rating != null && (
              <span>
                {RATING.format(q.rating)}★{q.review_count != null ? ` (${fmtInt(q.review_count)} reviews)` : ''}
              </span>
            )}
          </div>
        </div>
      </div>

      <Section title="Stock" onNavigate={onNavigate}
        link={links.restock ? { href: links.restock, label: 'Restock' } : undefined}>
        <div className="text-[13px] text-ink mb-1">{stockHeadline(q)}</div>
        {pools.map((p) => <Fact key={p.label} label={p.label} value={fmtInt(p.units)} />)}
        {q.on_order != null && q.on_order > 0 && <Fact label={onOrderLabel(q)} value={fmtInt(q.on_order)} />}
        <Fact label="Next purchase order" value={nextPoText(q)}
          className={q.next_po_overdue ? 'text-warning-soft-fg' : undefined} />
        {q.inventory_value != null && (
          <Fact label="Inventory value" value={`${pnlMoney(q.inventory_value)}${q.inventory_value_is_estimate ? ' (est.)' : ''}`}
            title="Units we own × landed cost per unit. On-order stock is not included." />
        )}
        {q.unit_cost != null && q.unit_cost_label && (
          <Fact label={q.unit_cost_label} value={fmtMoney(q.unit_cost)} />
        )}
      </Section>

      <Section title={windowCaption(q.window)} onNavigate={onNavigate}
        link={{ href: hubProductsLink(q.uid), label: 'Marketplace Hub' }}>
        <Sparkline points={q.trend_30d ?? []} />
        <Fact label="Units" value={fmtInt(q.units_30d)} />
        <Fact label="Sales" value={pnlMoney(q.sales_30d)} />
        <Fact label="Ad spend" value={pnlMoney(q.ad_spend_30d)} />
        <Fact label="ACoS" value={pnlPct(q.acos_30d)}
          title="Amazon ad spend ÷ Amazon ad-attributed sales (Sponsored Products, Brands, Display)." />
        <Fact label="TACoS" value={pnlPct(q.tacos_30d)} title="All ad spend ÷ sales." />
        <Fact label="Net profit" title={estTitle}
          value={`${pnlMoney(q.net_profit_30d)}${est ? ' (est.)' : ''}`}
          className={est ? 'text-warning-soft-fg' : pnlSignClass(q.net_profit_30d)} />
        <Fact label="Margin" title={estTitle}
          value={`${pnlPct(q.margin_30d)}${est ? ' (est.)' : ''}`}
          className={est ? 'text-warning-soft-fg' : undefined} />
      </Section>

      <Section title="Listing" onNavigate={onNavigate}
        link={links.listings ? { href: links.listings, label: 'Listings' } : undefined}>
        {statuses.length === 0
          ? <div className="text-[13px] text-ink-muted">Not listed on any marketplace we track.</div>
          : statuses.map(([mp, st]) => <Fact key={mp} label={mp} value={statusText(st)} />)}
        {links.amazon && (
          <div className="mt-1"><QvLink href={links.amazon} onNavigate={onNavigate}>Open on Amazon</QvLink></div>
        )}
      </Section>

      <div className="border-t border-edge mt-3 pt-3 flex flex-wrap gap-x-4 gap-y-1">
        {links.product_page && <QvLink href={links.product_page} onNavigate={onNavigate}>Product page</QvLink>}
        {links.ppc_product && <QvLink href={links.ppc_product} onNavigate={onNavigate}>PPC product</QvLink>}
        {links.settings && <QvLink href={links.settings} onNavigate={onNavigate}>Product settings</QvLink>}
      </div>
    </div>
  )
}

/** The drawer: one read, a skeleton while it loads, never blocks the page. */
export function ProductQuickViewDrawer({
  uid, marketplace, onClose,
}: {
  uid: string | null
  marketplace: string
  onClose: () => void
}) {
  const q = useProductQuickViewData(uid, marketplace)
  const data = q.data
  return (
    <Drawer
      open={!!uid}
      onClose={onClose}
      title={data?.product_name ?? uid ?? 'Product'}
      description={marketplace === 'all' ? 'All marketplaces' : marketplace}
      width="md"
    >
      {q.isError ? (
        <div className="text-sm text-danger">
          Could not load this product: {q.error.message}
        </div>
      ) : !data ? (
        <div className="flex flex-col gap-2" aria-busy="true">
          <div className="h-14 rounded bg-surface-2 animate-pulse" />
          <div className="h-24 rounded bg-surface-2 animate-pulse" />
          <div className="h-24 rounded bg-surface-2 animate-pulse" />
        </div>
      ) : (
        <QuickViewBody q={data} onNavigate={onClose} />
      )}
    </Drawer>
  )
}
